import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Button,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Switch,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Scenario, ScenarioMetadata, UpdateScenarioRequest, Co2MaterialSettingType, FeatureManagementType, MetadataEntityType, Model, ProblemDetails } from "@api/apiClient";
import { ScenarioMetadataForm } from "./ScenarioMetadataForm";
import RebarEstimationPage from "@features/settings/components/RebarEstimationPage/RebarEstimationPage";
import { useAdditionalMetadataFeature } from "@hooks/useAdditionalMetadataFeature";
import { useAppSettings } from "@api/appSettings";
import { useScenario, useScenarios } from "@api/scenarios";
import i18n from "@i18n/i18n";
import { observer } from "mobx-react";
import { DropDownOption } from "@/types/dropdown";
import useAppStore from "@hooks/useAppStore";
import Autocomplete from "@mui/material/Autocomplete";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import MicroChip from "@components/MicroChip/MicroChip";
import { useProfiles } from "@api/profiles";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import { useConfirm } from "material-ui-confirm";
import _ from "lodash";

export type ScenarioDialogMode = "edit" | "settings";

export interface ScenarioSettingsDialogProps {
  open: boolean;
  scenarioId: string | null;
  projectId: string | undefined;
  /** "edit" = full edit (name, description, models, etc.); "settings" = project phase, metadata, rebar only */
  mode: ScenarioDialogMode;
  onClose: () => void;
  onSave: (updateRequest: UpdateScenarioRequest) => Promise<void>;
}

const ScenarioSettingsDialog = observer((props: ScenarioSettingsDialogProps) => {
  const theme = useTheme();
  const appStore = useAppStore();
  const { open, scenarioId, projectId, mode, onClose, onSave } = props;
  const isSettingsOnly = mode === "settings";
  const { data: scenario, isLoading: scenarioLoading, error: scenarioError } = useScenario(
    open && scenarioId ? scenarioId : null
  );
  const scenarios = useScenarios(projectId ?? undefined);
  const { isEnabled: showMetadata } = useAdditionalMetadataFeature();
  const appSettings = useAppSettings();
  const { data: profileData } = useProfiles();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedModelIds, setSelectedModelIds] = useState<string[]>([]);
  const [selectedProjectPhaseValue, setSelectedProjectPhaseValue] = useState<string>("");
  const [selectedProfileValue, setSelectedProfileValue] = useState<string>("");
  const [selectedScenarioTypeId, setSelectedScenarioTypeId] = useState<string>("");
  const [scenarioTypeChangeReason, setScenarioTypeChangeReason] = useState<string>("");
  const [initialScenarioTypeId, setInitialScenarioTypeId] = useState<string>("");
  const [isFinalForProjectPhase, setIsFinalForProjectPhase] = useState(false);
  const [metadata, setMetadata] = useState<ScenarioMetadata>(
    new ScenarioMetadata({
      totalFloorArea: undefined,
      serviceLife: undefined,
    })
  );

  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [hasMetadataChanged, setHasMetadataChanged] = useState(false);
  const confirm = useConfirm();
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [duplicateScenarioError, setDuplicateScenarioError] = useState(false);
  const [modelSelectionError, setModelSelectionError] = useState(false);
  const [scenarioTypeReasonError, setScenarioTypeReasonError] = useState(false);
  const [scenarioTypeErrorMessage, setScenarioTypeErrorMessage] = useState("");

  const projectPhaseOptions: DropDownOption[] = useMemo(
    () =>
      appStore.applicationSettings?.metadataEntities
        ?.filter((entity) => entity.type === MetadataEntityType.ProjectPhase)
        .map((entity) => ({
          key: entity.metadataEntityId as string,
          label: entity.name as string,
        })) ?? [],
    [appStore.applicationSettings?.metadataEntities]
  );

  const profileOptions = useMemo(() => {
    if (!appSettings.data?.profileLevelRebarSettings || !profileData?.length) {
      return [{ label: i18n.t("scenarios-tab.scenario-dialog.profile-input.placeholder"), value: "" }];
    }
    return [
      { label: i18n.t("scenarios-tab.scenario-dialog.profile-input.placeholder"), value: "" },
      ...profileData.map((p) => ({ label: p.name ?? "", value: p.profileId ?? "" })),
    ];
  }, [appSettings.data?.profileLevelRebarSettings, profileData]);

  const scenarioTypeOptions: DropDownOption[] = useMemo(
    () =>
      appStore.applicationSettings?.metadataEntities
        ?.filter((entity) => entity.type === MetadataEntityType.ScenarioType)
        .map((entity) => ({
          key: entity.metadataEntityId as string,
          label: entity.name as string,
        })) ?? [],
    [appStore.applicationSettings?.metadataEntities]
  );

  const defaultScenarioTypeId = useMemo(
    () => scenarioTypeOptions.find((o) => o.label?.toLowerCase() === "none")?.key ?? "",
    [scenarioTypeOptions]
  );

  const finalScenarioTypeId = useMemo(
    () => scenarioTypeOptions.find((o) => o.label?.toLowerCase() === "final")?.key ?? "",
    [scenarioTypeOptions]
  );

  const isScenarioTypeFinal = !!finalScenarioTypeId && selectedScenarioTypeId === finalScenarioTypeId;

  const isScenarioFinal =
    (!!finalScenarioTypeId && scenario?.scenarioTypeId === finalScenarioTypeId) ||
    scenario?.scenarioTypeEntity?.name?.toLowerCase() === "final";

  const finalScenarioInProject = useMemo(
    () =>
      scenarios.data?.find(
        (s: Scenario) =>
          s.scenarioTypeId === finalScenarioTypeId ||
          s.scenarioTypeEntity?.name?.toLowerCase() === "final"
      ),
    [finalScenarioTypeId, scenarios.data]
  );

  const hasFinalScenarioConflict =
    isScenarioTypeFinal &&
    !!finalScenarioInProject &&
    finalScenarioInProject.scenarioId !== scenario?.scenarioId;

  const initialWasFinal = !!finalScenarioTypeId && initialScenarioTypeId === finalScenarioTypeId;
  const isChangingFinalToNonFinal =
    initialWasFinal && selectedScenarioTypeId !== finalScenarioTypeId;
  const isChangingNonFinalToFinal =
    !initialWasFinal && isScenarioTypeFinal;

  const shouldShowFinalStatusInfo =
    !!scenario?.scenarioTypeLastModifiedAt && (isScenarioTypeFinal || initialWasFinal);

  const projectModels = appStore.project?.models ?? [];
  const selectedModels = useMemo(
    () => projectModels.filter((m) => selectedModelIds.includes(m.modelId!)),
    [projectModels, selectedModelIds]
  );

  const settingsHasAnyChanges =
    isSettingsOnly &&
    (hasMetadataChanged || selectedProjectPhaseValue !== (scenario?.projectPhaseId ?? ""));

  // Reset form when scenario loads
  useEffect(() => {
    if (!scenario) return;
    setName(scenario.name ?? "");
    setDescription(scenario.description ?? "");
    setSelectedModelIds(scenario.scenarioModels?.map((sm) => sm.modelId!).filter(Boolean) ?? []);
    setSelectedProjectPhaseValue(scenario.projectPhaseId ?? "");
    setSelectedProfileValue(scenario.profileId ?? "");
    setSelectedScenarioTypeId(scenario.scenarioTypeId ?? defaultScenarioTypeId ?? "");
    setScenarioTypeChangeReason(scenario.scenarioTypeChangeReason ?? "");
    setInitialScenarioTypeId(scenario.scenarioTypeId ?? defaultScenarioTypeId ?? "");
    setIsFinalForProjectPhase(scenario.isFinalForProjectPhase ?? false);
    if (scenario.scenarioMetadata) {
      setMetadata(
        new ScenarioMetadata({
          totalFloorArea: scenario.scenarioMetadata.totalFloorArea,
          serviceLife: scenario.scenarioMetadata.serviceLife,
        })
      );
    }
    setModelSelectionError(false);
    setDuplicateScenarioError(false);
    setScenarioTypeReasonError(false);
    setScenarioTypeErrorMessage("");
    if (isSettingsOnly) setHasMetadataChanged(false);
  }, [scenario, isSettingsOnly, defaultScenarioTypeId]);

  // Duplicate name check (same as add): exclude current scenario
  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setName(value);
    const trimmed = value?.trim().toLowerCase();
    if (trimmed && scenarioId && scenarios.data) {
      const hasMatch = scenarios.data.some(
        (s: Scenario) =>
          s.scenarioId !== scenarioId && s.name?.toLocaleLowerCase() === trimmed
      );
      setDuplicateScenarioError(hasMatch);
    } else {
      setDuplicateScenarioError(false);
    }
  };

  const handleMetadataChange = (field: string, value: number | undefined) => {
    setMetadata((prev) => {
      const newData = {
        totalFloorArea: prev.totalFloorArea,
        serviceLife: prev.serviceLife,
        [field]: value,
      };
      return new ScenarioMetadata(newData);
    });
    if (isSettingsOnly) setHasMetadataChanged(true);
  };

  const handleModelInputChange = (_event: unknown, value: Model[]) => {
    const ids = value?.map((v: Model) => v.modelId!);
    setSelectedModelIds(ids ?? []);
    setModelSelectionError(!ids?.length);
  };

  const validate = (): boolean => {
    const trimmedName = name?.trim();
    const trimmedDesc = description?.trim();
    if (!trimmedName) return false;
    if (!trimmedDesc) return false;
    if (duplicateScenarioError) return false;
    if (selectedModelIds.length === 0) {
      setModelSelectionError(true);
      return false;
    }
    if (isChangingFinalToNonFinal && _.isEmpty(scenarioTypeChangeReason?.trim())) {
      setScenarioTypeReasonError(true);
      return false;
    }
    return true;
  };

  const handleSettingsSave = async () => {
    if (!scenarioId || !scenario) return;
    try {
      setIsSubmitting(true);
      setShowErrorMessage(false);
      const modelIds = scenario.scenarioModels?.map((sm) => sm.modelId!).filter(Boolean) ?? [];
      const updateRequest = new UpdateScenarioRequest({
        scenarioId,
        name: scenario.name,
        description: scenario.description ?? "",
        modelIds: modelIds.length > 0 ? modelIds : undefined,
        scenarioMetadata: new ScenarioMetadata({
          totalFloorArea: metadata.totalFloorArea,
          serviceLife: metadata.serviceLife,
        }),
        projectPhaseId: selectedProjectPhaseValue || undefined,
        scenarioTypeId: selectedScenarioTypeId || undefined,
        scenarioTypeChangeReason: isChangingFinalToNonFinal
          ? scenarioTypeChangeReason?.trim() || undefined
          : undefined,
      });
      await onSave(updateRequest);
      setHasMetadataChanged(false);
      setShowSuccessMessage(true);
    } catch (error: unknown) {
      console.error("Error updating scenario settings:", error);
      const problemDetailsMessage =
        (error as { response?: { data?: { detail?: string; title?: string } } })?.response
          ?.data?.detail ??
        (error as { response?: { data?: { detail?: string; title?: string } } })?.response
          ?.data?.title;

      if (problemDetailsMessage) {
        setScenarioTypeErrorMessage(String(problemDetailsMessage));
      }
      const msg =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        (error as Error)?.message ??
        "Failed to update scenario settings.";
      setErrorMessage(String(msg));
      setShowErrorMessage(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!scenarioId || !scenario) return;
    setIsSubmitted(true);
    setScenarioTypeErrorMessage("");
    if (!validate()) {
      return;
    }
    const initialModelIds = scenario.scenarioModels?.map((sm) => sm.modelId!).filter(Boolean) ?? [];
    const modelsChanged =
      initialModelIds.length !== selectedModelIds.length ||
      initialModelIds.some((id) => !selectedModelIds.includes(id)) ||
      selectedModelIds.some((id) => !initialModelIds.includes(id));

    if (modelsChanged) {
      const { confirmed } = await confirm({
        title: i18n.t("scenarios-grid.edit-dialog.models-changed-confirm-title"),
        description: i18n.t("scenarios-grid.edit-dialog.models-changed-affects-calculations"),
        confirmationText: i18n.t("dialog.ok"),
        cancellationText: i18n.t("dialog.cancel"),
        dialogProps: { maxWidth: "xs" },
        confirmationButtonProps: { variant: "contained", color: "primary" },
        cancellationButtonProps: { variant: "outlined" },
      });
      if (!confirmed) {
        setIsSubmitted(false);
        return;
      }
    }

    try {
      setIsSubmitting(true);
      setShowErrorMessage(false);
      const updateRequest = new UpdateScenarioRequest({
        scenarioId,
        name: name.trim(),
        description: description.trim(),
        modelIds: selectedModelIds,
        scenarioMetadata: new ScenarioMetadata({
          totalFloorArea: metadata.totalFloorArea,
          serviceLife: metadata.serviceLife,
        }),
        projectPhaseId: selectedProjectPhaseValue || undefined,
        scenarioTypeId: selectedScenarioTypeId || undefined,
        scenarioTypeChangeReason: isChangingFinalToNonFinal ? scenarioTypeChangeReason?.trim() || undefined : undefined,
      });
      await onSave(updateRequest);
      setShowSuccessMessage(true);
      handleClose();
    } catch (error: unknown) {
      console.error("Error updating scenario:", error);
      const dex = error as ProblemDetails;
      if (dex && dex.status === 409) {
        //Conflict, due to scenario type change to final when another final scenario exists
        setScenarioTypeErrorMessage(String(dex.detail));
      } else {
        const msg =
          (error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? (error as Error)?.message ?? "Failed to update scenario. Please try again.";
        setErrorMessage(String(msg));
        setShowErrorMessage(true);
      }
     
    } finally {
      setIsSubmitting(false);
      setIsSubmitted(false);
    }
  };

  const handleClose = () => {
    setName("");
    setDescription("");
    setSelectedModelIds([]);
    setDuplicateScenarioError(false);
    setModelSelectionError(false);
    setHasMetadataChanged(false);
    setScenarioTypeChangeReason("");
    setScenarioTypeReasonError(false);
    setScenarioTypeErrorMessage("");
    setInitialScenarioTypeId("");
    setMetadata(
      new ScenarioMetadata({
        totalFloorArea: undefined,
        serviceLife: undefined,
      })
    );
    setErrorMessage("");
    onClose();
  };

  return (
    <>
      <Dialog
        fullWidth
        maxWidth={isSettingsOnly ? "xl" : "sm"}
        open={open}
        onClose={handleClose}
        PaperProps={isSettingsOnly ? { sx: { height: "80%" } } : undefined}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {isSettingsOnly
            ? i18n.t("scenarios-grid.settings-button.title")
            : i18n.t("scenarios-tab.scenario-dialog.edit-title")}
          <IconButton onClick={handleClose} disabled={isSubmitting}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={
            isSettingsOnly
              ? {
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  paddingLeft: 2,
                  paddingRight: 2,
                  paddingBottom: 0,
                  gap: 2,
                  overflow: "auto",
                }
              : undefined
          }
        >
          {scenarioLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {scenarioError && (
            <Alert severity="error" onClose={handleClose}>
              {i18n.t("scenarios-grid.edit-dialog.load-error") || "Failed to load scenario."}
            </Alert>
          )}
          {!scenarioLoading && !scenarioError && scenario && isSettingsOnly && (
            <>
              <Box>
                {appStore.hasFeature(FeatureManagementType.ProjectPhase) && (
                  <FormControl fullWidth sx={{ mb: 2, mt: 2 }}>
                    <InputLabel>{i18n.t("scenarios-tab.scenario-dialog.project-phase-input.title")}</InputLabel>
                    <Select
                      value={selectedProjectPhaseValue ?? ""}
                      label={i18n.t("scenarios-tab.scenario-dialog.project-phase-input.title")}
                      onChange={(event) => {
                        const newProjectPhaseId = event.target.value;
                        setSelectedProjectPhaseValue(newProjectPhaseId);
                      }}
                      disabled={isSubmitting}
                    >
                      {projectPhaseOptions.map((opt) => (
                        <MenuItem key={opt.key} value={opt.key}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {showMetadata && (
                  <ScenarioMetadataForm metadata={metadata} onChange={handleMetadataChange} />
                )}
                {settingsHasAnyChanges && (
                  <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
                    <Button variant="contained" onClick={handleSettingsSave} disabled={isSubmitting}>
                      {isSubmitting
                        ? i18n.t("project-settings-page.save-metadata-button.on-saving")
                        : i18n.t("project-settings-page.save-metadata-button.title")}
                    </Button>
                  </Box>
                )}
              </Box>
              <Box sx={{ flexGrow: 1, display: "flex" }}>
                <RebarEstimationPage
                  originScreen="Scenario"
                  mode={
                    appSettings.data?.profileLevelRebarSettings
                      ? Co2MaterialSettingType.Profile
                      : Co2MaterialSettingType.Scenario
                  }
                  projectId={scenario?.projectId}
                  scenarioId={scenario?.scenarioId}
                  profileId={scenario?.profileId}
                  rebarEnabled={scenario?.profile?.rebarEstimationStatus}
                />
              </Box>
            </>
          )}
          {!scenarioLoading && !scenarioError && scenario && !isSettingsOnly && (
            <form onSubmit={handleSubmit}>
              <Stack mt={1} spacing={2}>
                <TextField
                  label={i18n.t("scenarios-tab.scenario-dialog.name-input.title")}
                  name="name"
                  value={name}
                  onChange={handleNameChange}
                  margin="normal"
                  variant="outlined"
                  fullWidth
                  required
                  disabled={isSubmitting}
                  error={(isSubmitted && _.isEmpty(name?.trim())) || duplicateScenarioError}
                  helperText={
                    isSubmitted && _.isEmpty(name?.trim())
                      ? i18n.t("scenario.helper.scenario-name")
                      : duplicateScenarioError
                        ? i18n.t("scenarios-tab.scenario-dialog.name-input.duplicate")
                        : ""
                  }
                />
                <TextField
                  label={i18n.t("scenarios-tab.scenario-dialog.description-input.title")}
                  name="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  margin="normal"
                  variant="outlined"
                  fullWidth
                  required
                  disabled={isSubmitting}
                  error={isSubmitted && _.isEmpty(description?.trim())}
                  helperText={
                    isSubmitted && _.isEmpty(description?.trim())
                      ? i18n.t("scenario.helper.scenario-description")
                      : ""
                  }
                />

                {showMetadata && (
                  <ScenarioMetadataForm metadata={metadata} onChange={handleMetadataChange} />
                )}

                {appSettings.data?.profileLevelRebarSettings && (
                  <FormControl fullWidth margin="normal" disabled={true}>
                    <InputLabel>{i18n.t("scenarios-tab.scenario-dialog.profile-input.title")}</InputLabel>
                    <Select
                      value={selectedProfileValue}
                      label={i18n.t("scenarios-tab.scenario-dialog.profile-input.title")}
                      onChange={(e) => setSelectedProfileValue(e.target.value)}
                    >
                      {profileOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  options={projectModels}
                  value={selectedModels}
                  getOptionKey={(option: Model) => option.modelId!}
                  getOptionLabel={(option: Model) =>
                    `${option?.modelName ?? ""} (${option?.modelVersion ?? ""})`
                  }
                  isOptionEqualToValue={(a, b) => a.modelId === b.modelId}
                  onChange={handleModelInputChange}
                  renderOption={(props: unknown, option: Model, { selected }) => (
                    <Box
                      {...(props as object)}
                      key={option.modelId}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        padding: "8px",
                        alignItems: "center",
                        minWidth: "200px",
                      }}
                    >
                      <Checkbox checked={selected} />
                      <Box sx={{ flex: 1, display: "flex", justifyContent: "space-between" }}>
                        <Typography variant="body2">{option?.modelName}</Typography>
                        <MicroChip label={option?.modelVersion ?? ""} />
                      </Box>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={i18n.t("scenarios-tab.scenario-dialog.models-select.input.title")}
                      placeholder={i18n.t("scenarios-tab.scenario-dialog.models-select.input.placeholder")}
                      error={modelSelectionError}
                      helperText={
                        modelSelectionError
                          ? i18n.t("scenarios-tab.scenario-dialog.models-select.input.helper-text")
                          : ""
                      }
                    />
                  )}
                  disabled={isSubmitting}
                  renderTags={(value, getTagProps) =>
                    value.map((option: Model, index: number) => {
                      return (
                        <Chip
                          {...getTagProps({ index })}
                          key={option.modelId}
                          label={
                            <>
                              {option?.modelName}
                              <MicroChip
                                label={option?.modelVersion ?? ""}
                                sx={{ marginLeft: 1, height: "20px" }}
                              />
                            </>
                          }
                          sx={{ alignItems: "center" }}
                        />
                      );
                    })
                  }
                />

                <Card elevation={3} sx={{ "& .MuiCardHeader-root": { backgroundColor: theme.palette.background.default } }}>
                  {(appStore.hasFeature(FeatureManagementType.ProjectPhase) ||
                    (!appSettings.data?.profileLevelRebarSettings && appSettings.data?.rebarEstimation)) && (
                    <CardHeader
                      title={
                        <Typography variant="body2">
                          {i18n.t("scenarios-tab.title.scenario-settings")}
                        </Typography>
                      }
                      elevation={3}
                    />
                  )}
                  {appStore.hasFeature(FeatureManagementType.ProjectPhase) && (
                    <>
                      <CardContent>
                        <FormControl fullWidth>
                          <InputLabel>{i18n.t("scenarios-tab.scenario-dialog.project-phase-input.title")}</InputLabel>
                          <Select
                            value={selectedProjectPhaseValue}
                            label={i18n.t("scenarios-tab.scenario-dialog.project-phase-input.title")}
                            onChange={(e) => setSelectedProjectPhaseValue(e.target.value)}
                            disabled={isSubmitting}
                          >
                            <MenuItem value="">{i18n.t("scenarios-tab.scenario-dialog.profile-input.placeholder")}</MenuItem>
                            {projectPhaseOptions.map((option) => (
                              <MenuItem key={option.key} value={option.key}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </CardContent>
                      <CardContent
                        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
                      >
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          <Typography variant="body1">
                            {i18n.t("scenarios-tab.scenario-dialog.final-for-phase.title")}
                          </Typography>
                        </Box>
                        <Switch
                          checked={isFinalForProjectPhase}
                          onChange={(e) => setIsFinalForProjectPhase(e.target.checked)}
                          disabled={isSubmitting}
                        />
                      </CardContent>
                    </>
                  )}
                </Card>

                {appStore.hasFeature(FeatureManagementType.ScenarioType) && (
                  <FormControl fullWidth margin="normal" disabled={isSubmitting}>
                    <InputLabel>{i18n.t("scenarios-tab.scenario-dialog.scenario-type-input.title")}</InputLabel>
                    <Select
                      value={selectedScenarioTypeId || defaultScenarioTypeId}
                      label={i18n.t("scenarios-tab.scenario-dialog.scenario-type-input.title")}
                      onChange={(e) => {
                        const nextScenarioTypeId = e.target.value as string;
                        setSelectedScenarioTypeId(nextScenarioTypeId);
                        if (!(initialWasFinal && nextScenarioTypeId !== finalScenarioTypeId)) {
                          setScenarioTypeChangeReason("");
                          setScenarioTypeReasonError(false);
                        }
                        setScenarioTypeErrorMessage("");
                      }}
                    >
                      {scenarioTypeOptions.map((opt) => (
                        <MenuItem key={opt.key} value={opt.key}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {appStore.hasFeature(FeatureManagementType.ScenarioType) &&
                  !scenarioTypeErrorMessage &&
                  hasFinalScenarioConflict &&
                  isChangingNonFinalToFinal && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <WarningAmberIcon color="warning" fontSize="small" />
                      <Typography variant="caption" color="warning.main">
                        {i18n.t("scenarios-tab.scenario-dialog.scenario-type-final-warning", {
                          name: finalScenarioInProject?.name ?? "-",
                        })}
                      </Typography>
                    </Box>
                  )}

                {appStore.hasFeature(FeatureManagementType.ScenarioType) && scenarioTypeErrorMessage && (
                  <Typography variant="caption" color="error" component="span">
                    {scenarioTypeErrorMessage}
                  </Typography>
                )}

                {appStore.hasFeature(FeatureManagementType.ScenarioType) &&
                  shouldShowFinalStatusInfo && (
                    <Typography variant="caption" color="text.secondary">
                      {i18n.t("scenarios-tab.scenario-dialog.scenario-type-final-info", {
                        date: i18n.t("datetime_long", {
                          value: scenario.scenarioTypeLastModifiedAt,
                        }),
                        user: scenario.scenarioTypeLastModifiedByUserName ?? "-",
                      })}
                    </Typography>
                  )}

                  {appStore.hasFeature(FeatureManagementType.ScenarioType) && isChangingFinalToNonFinal && (
                    <TextField
                      label={i18n.t("scenarios-tab.scenario-dialog.scenario-type-reason.title")}
                      name="scenarioTypeChangeReason"
                      value={scenarioTypeChangeReason}
                      onChange={(e) => {
                        setScenarioTypeChangeReason(e.target.value);
                        setScenarioTypeReasonError(false);
                      }}
                      margin="normal"
                      variant="outlined"
                      fullWidth
                      required
                      multiline
                      minRows={3}
                      disabled={isSubmitting}
                      error={scenarioTypeReasonError}
                      helperText={
                        scenarioTypeReasonError
                          ? i18n.t("scenarios-tab.scenario-dialog.scenario-type-reason.helper-text")
                          : ""
                      }
                    />
                  )}

                <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
                  {/* {isScenarioFinal && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      {i18n.t("scenarios-tab.scenario-dialog.final-edit-disabled") ??
                        "Final scenarios cannot be edited."}
                    </Alert>
                  )} */}
                  <Grid container spacing={2} sx={{ justifyContent: "end" }}>
                    <Grid>
                      <Button variant="outlined" onClick={handleClose} disabled={isSubmitting}>
                        {i18n.t("scenarios-tab.scenario-dialog.close-button.title")}
                      </Button>
                    </Grid>
                    <Grid>
                      <Button
                        autoFocus
                        disabled={isSubmitting || duplicateScenarioError }
                        variant="contained"
                        type="submit"
                      >
                        {i18n.t("scenarios-tab.scenario-dialog.save-button.title")}
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </Stack>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={showSuccessMessage}
        autoHideDuration={3000}
        onClose={() => setShowSuccessMessage(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="success" variant="filled" onClose={() => setShowSuccessMessage(false)}>
          {i18n.t("scenarios-grid.edit-dialog.saved") ?? "Scenario updated successfully!"}
        </Alert>
      </Snackbar>

      <Snackbar
        open={showErrorMessage}
        autoHideDuration={5000}
        onClose={() => setShowErrorMessage(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={() => setShowErrorMessage(false)}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
});

export default ScenarioSettingsDialog;
